/**
 * The one place that knows an entity's URL.
 *
 * Every detail route is a Jira-style id behind a path prefix — /vendor/VD-881,
 * /bookings/VB-019 — but the prefix used to live as a string literal at each
 * of ~80 call sites. Two things then went wrong together:
 *
 *   1. A caller that forgot fmtId produced /vendor/881, and
 *   2. parseId happily accepts a bare numeric, so the page still loaded.
 *
 * A wrong URL that works is a wrong URL nobody reports. Global search had
 * been building raw-id paths for vendors, events, fabrics, guests, tasks,
 * procurement orders and stationery for exactly that reason — only quotes
 * went through fmtId.
 *
 * So the mapping lives here, keyed by entity, and callers ask for a path
 * rather than assembling one. Adding a route means adding a line here, and
 * the prefix cannot drift from the one App.tsx parses because there is only
 * one copy of it.
 *
 * No route carries an /edit suffix. A detail view and its edit form are the
 * same page here, so /edit only ever added a segment the router did not need
 * — App.tsx already treats any /task/<id> as the task form.
 */
import { fmtId, parseId } from './jiraId';

/** Prefix each entity's ids carry, matching what App.tsx's parseId expects. */
export const ENTITY_PREFIX = {
  vendor: 'VD',
  quote: 'QT',
  task: 'TA',
  shippingAgent: 'SA',
  budgetCategory: 'BC',
  catalogueItem: 'CT',
  vehicle: 'VH',
  driver: 'DR',
  guideline: 'GL',
  stationeryItem: 'ST',
  decorElement: 'DC',
  calendarEvent: 'CE',
  event: 'EV',
  menuItem: 'MI',
  venue: 'VN',
  client: 'CL',
  clientInvoice: 'IV',
  clientQuote: 'CQ',
  payment: 'PY',
  contributor: 'CN',
  bundle: 'BN',
  project: 'PJ',
  song: 'SG',
  teamMember: 'TM',
  transportAssignment: 'TR',
  favourAssignment: 'FA',
  inventoryItem: 'IT',
  fabric: 'FB',
  fabricAssignment: 'AOS',
  asoebiDelivery: 'AD',
  asoebiAssignment: 'AZ',
  guest: 'GS',
  procurementOrder: 'PO',
  shipment: 'SH',
  vendorBooking: 'BK',
} as const;

export type EntityKind = keyof typeof ENTITY_PREFIX;

/** Where each entity's detail view lives. The formatted id is appended, and
 *  any suffix (such as /profile) is part of the template. */
const ROUTE: Partial<Record<EntityKind, (formattedId: string) => string>> = {
  vendor: id => `/vendor/${id}`,
  quote: id => `/quotes/${id}`,
  task: id => `/task/${id}`,
  shippingAgent: id => `/procurement/shipping-agents/${id}`,
  budgetCategory: id => `/budget/category/${id}`,
  catalogueItem: id => `/catalogue/${id}`,
  vehicle: id => `/vehicle/${id}`,
  driver: id => `/driver/${id}`,
  guideline: id => `/guidelines/${id}`,
  stationeryItem: id => `/stationery-item/${id}`,
  decorElement: id => `/decor/${id}`,
  calendarEvent: id => `/calendar/event/${id}`,
  event: id => `/events/${id}`,
  menuItem: id => `/menu-item/${id}`,
  venue: id => `/venue/${id}`,
  client: id => `/clients/${id}`,
  clientInvoice: id => `/invoices/${id}`,
  clientQuote: id => `/client-quotes/${id}`,
  payment: id => `/payments/record/${id}`,
  contributor: id => `/payments/contributor/${id}`,
  bundle: id => `/bundle/${id}`,
  procurementOrder: id => `/procurement/orders/${id}`,
  shipment: id => `/procurement/shipments/${id}`,
  vendorBooking: id => `/vendor-bookings/${id}`,
  teamMember: id => `/team-member/${id}`,
  fabric: id => `/asoebi/fabrics/${id}`,
  fabricAssignment: id => `/asoebi/orders/${id}`,
  asoebiDelivery: id => `/asoebi/deliveries/${id}`,
  asoebiAssignment: id => `/asoebi/assignments/${id}`,
  // project and song have an id and a list column but no detail page of
  // their own yet, so entityPath returns undefined for them.
};

/**
 * Path to an entity's detail view, with its id correctly prefixed.
 * Returns undefined for a missing id so callers can fall back to a list
 * view rather than navigating to a malformed URL.
 */
export function entityPath(kind: EntityKind, id: string | number | null | undefined): string | undefined {
  if (id === null || id === undefined || id === '') return undefined;
  const route = ROUTE[kind];
  return route ? route(fmtId(ENTITY_PREFIX[kind], id)) : undefined;
}

/** The form that adds a vendor to the active project. */
export const NEW_VENDOR_PATH = '/vendor/suggest';

/** The vendor's profile page — a distinct view from the vendor detail page. */
export function vendorProfilePath(id: string | number | null | undefined): string | undefined {
  const base = entityPath('vendor', id);
  return base ? `${base}/profile` : undefined;
}

/** The vendor's rating page. */
export function vendorRatingPath(id: string | number | null | undefined): string | undefined {
  const base = entityPath('vendor', id);
  return base ? `${base}/rate` : undefined;
}

/**
 * Which record a detail URL names, the reverse of entityPath: '/vendor/VD-881'
 * -> { kind: 'vendor', id: '881' }. Undefined for list routes, the `new` forms
 * and sub-pages (/vendor/VD-881/profile), so a bar only offers record
 * navigation on the record's own page.
 */
export function parseEntityPath(pathname: string): { kind: EntityKind; id: string } | undefined {
  const clean = pathname.split(/[?#]/)[0].replace(/\/+$/, '');
  for (const kind of Object.keys(ROUTE) as EntityKind[]) {
    const [head, tail] = ROUTE[kind]!('\u0000').split('\u0000');
    if (tail || !clean.startsWith(head)) continue;
    const segment = clean.slice(head.length);
    if (!segment || segment.includes('/')) continue;
    if (!segment.toUpperCase().startsWith(`${ENTITY_PREFIX[kind]}-`)) continue;
    const id = parseId(ENTITY_PREFIX[kind], segment);
    if (id) return { kind, id };
  }
  return undefined;
}
